import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Loader2, ArrowUpRight } from "lucide-react";
import { getAskBox, askReadoo, getRelated } from "@/lib/api";
import { articlePath } from "@/lib/readoo";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function browserId() {
  try {
    let id = localStorage.getItem("readoo_bid");
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now();
      localStorage.setItem("readoo_bid", id);
    }
    return id;
  } catch {
    return "";
  }
}

function ServiceInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <p data-testid="ask-service-note" className="mt-3 text-xs text-subtle">
        Readoo usa l’AI per rispondere velocemente alle tue domande.{" "}
        <button type="button" onClick={() => setOpen(true)} data-testid="ask-service-more" className="font-semibold text-brand hover:underline">Scopri di più.</button>
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ask-service-dialog" className="p-0 overflow-hidden bg-surfaceSecondary border-hair text-onSurface w-[calc(100%-40px)] max-w-2xl rounded-2xl">
          <DialogTitle className="px-6 py-4 bg-surfaceTertiary text-base font-semibold border-b border-hair">Informazioni sul servizio</DialogTitle>
          <DialogDescription asChild>
            <p className="px-6 pt-4 pb-6 text-sm leading-relaxed text-onSurfaceSecondary">
              Questo servizio si avvale dell’uso delle API di OpenAI per rispondere alle tue richieste.{" "}
              <strong className="text-onSurface">Non è necessario che tu fornisca alcun dato personale</strong>, qualora tu volontariamente decida di inserirli questi verranno trattati in conformità con la normativa applicabile. Cerca di formulare una domanda quanto più specifica possibile sull’argomento di tuo interesse. La risposta contiene informazioni relative alla tua richiesta. Ricordati che questo assistente è in fase sperimentale e potrebbe generare risposte non completamente corrette.
            </p>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RelatedLinks({ items: base, update }) {
  const items = update ? [{ ...update, _update: true }, ...base.filter((r) => r.article_id !== update.article_id)] : base;
  if (!items.length) return null;
  return (
    <div data-testid="ask-related" className="mt-3 pt-3 border-t border-hair">
      <p className="font-mono text-[10px] font-bold uppercase tracking-wider text-subtle mb-1.5">Approfondisci su Readoo</p>
      <ul className="space-y-1">
        {items.map((r) => (
          <li key={r.article_id}>
            <Link to={articlePath(r)} data-testid={`ask-related-${r.article_id}`} className="group inline-flex items-start gap-1 text-[15px] text-brand hover:underline">
              <ArrowUpRight size={15} className="mt-1 shrink-0" />
              <span>
                {r._update && (
                  <span data-testid="ask-related-update-badge" className="mr-1.5 inline-block align-middle px-1.5 py-0.5 rounded bg-brand text-onBrand font-mono text-[9px] font-bold uppercase tracking-wider no-underline">
                    Aggiornamento{r.published_at ? ` · ${new Date(r.published_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}` : ""}
                  </span>
                )}
                {r.title}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AskReadooBox({ articleId }) {
  const bid = useRef(browserId());
  const [suggestions, setSuggestions] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [active, setActive] = useState(false);
  const [maxDay, setMaxDay] = useState(3);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [relevant, setRelevant] = useState(true);
  const [turns, setTurns] = useState([]);
  const [related, setRelated] = useState([]);
  const [unlimited, setUnlimited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setAnswer("");
    setTurns([]);
    getRelated(articleId, 2).then((r) => alive && setRelated((r.related || r.articles || []).slice(0, 2))).catch(() => {});
    getAskBox(articleId, bid.current)
      .then((r) => {
        if (!alive) return;
        setSuggestions(r.suggestions || []);
        setRemaining(r.remaining ?? 0);
        setActive(!!r.active);
        setMaxDay(r.max ?? 3);
        setUnlimited(!!r.unlimited);
      })
      .catch(() => setActive(false));
    return () => { alive = false; };
  }, [articleId]);

  const submit = async (q) => {
    const text = (q ?? question).trim();
    if (text.length < 3 || loading || remaining <= 0) return;
    setQuestion(text);
    setLoading(true);
    setError("");
    setAnswer("");
    try {
      const history = turns.slice(-3).map((t) => ({ q: t.q, a: t.a }));
      const r = await askReadoo(articleId, text, bid.current, history);
      if (r.blocked) {
        setRemaining(0);
        setError(r.global_limit ? "Servizio molto richiesto oggi, riprova domani." : "Hai esaurito le domande di oggi. Torna domani!");
        return;
      }
      setAnswer(r.answer || "");
      setRelevant(r.relevant !== false);
      if (r.answer) setTurns((t) => [...t, { id: `${Date.now()}-${t.length}`, q: text, a: r.answer, relevant: r.relevant !== false, web: !!r.web, update: r.update || null }].slice(-3));
      setQuestion("");
      if (typeof r.remaining === "number") setRemaining(r.remaining);
    } catch (e) {
      setError(e.message || "Servizio non disponibile, riprova.");
    } finally {
      setLoading(false);
    }
  };

  // Box nascosto del tutto se non attivo (domande esaurite o tetto globale raggiunto) al caricamento
  if (!active && remaining <= 0 && !answer && !error) return null;

  return (
    <div data-testid="ask-readoo-box" className="my-8 bg-surfaceSecondary border-2 border-brand/30 rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="flex items-center gap-2">
          <Sparkles size={16} className="text-brand" />
          <span className="font-mono text-xs font-bold uppercase tracking-wider text-brand">Chiedi a Readoo</span>
        </span>
        <span data-testid="ask-readoo-remaining" className="text-[11px] font-mono text-subtle">
          {unlimited ? <span className="text-brand font-semibold">Superadmin · illimitate</span> : <><span className="text-brand font-semibold">{remaining}</span> / {maxDay} oggi</>}
        </span>
      </div>
      <p className="text-sm text-subtle mb-4">Fai una domanda su questo articolo o sul suo argomento: l'AI risponde in modo pertinente.</p>

      {suggestions.some((s) => !turns.find((t) => t.q === s)) && remaining > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {suggestions.map((s, i) => turns.find((t) => t.q === s) ? null : (
            <button
              key={s}
              data-testid={`ask-suggestion-${i}`}
              onClick={() => submit(s)}
              disabled={remaining <= 0}
              className="text-[15px] px-3.5 py-1.5 rounded-full border border-hair bg-surface hover:border-brand/50 hover:text-brand text-onSurfaceSecondary transition-colors disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {remaining > 0 && (
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex gap-2">
          <input
            data-testid="ask-readoo-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={300}
            placeholder={turns.length ? "Fai un’altra domanda…" : "Scrivi la tua domanda…"}
            className="flex-1 min-w-0 bg-surface border border-hair rounded-full px-4 py-2.5 text-[17px] text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand transition-colors"
          />
          <button
            data-testid="ask-readoo-submit-button"
            type="submit"
            disabled={loading || question.trim().length < 3}
            className="shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-brand text-onBrand hover:bg-brandSecondary disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      )}

      {error && <p data-testid="ask-error" className="mt-3 text-sm text-bad">{error}</p>}
      {turns.length > 0 && (
        <div data-testid="ask-answer" className="mt-4 bg-surface border border-hair rounded-xl p-4 space-y-4">
          {turns.map((t, i) => (
            <div key={t.id} data-testid={`ask-turn-${i}`}>
              <p className="text-sm font-semibold text-onSurfaceSecondary mb-1.5">
                {t.q}
                {t.web && <span data-testid={`ask-turn-web-${i}`} className="ml-2 align-middle font-mono text-[10px] font-bold uppercase tracking-wider text-brand">· aggiornato dal web</span>}
              </p>
              <p className={`text-[17px] leading-relaxed whitespace-pre-line border-l-2 pl-3 ${t.relevant ? "border-brand text-onSurface" : "border-hairStrong text-subtle"}`}>{t.a}</p>
            </div>
          ))}
          {turns[turns.length - 1]?.relevant && <RelatedLinks items={related} update={turns[turns.length - 1].update} />}
        </div>
      )}
      <ServiceInfo />
    </div>
  );
}
